"use client"

import { useState, useEffect, useRef } from "react"
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
})

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

// Component to handle map clicks
const LocationMarker = ({ position, setPosition, onLocationSelect }) => {
  useMapEvents({
    click(e) {
      const newPosition = [e.latlng.lat, e.latlng.lng]
      setPosition(newPosition)
      onLocationSelect({
        lat: e.latlng.lat,
        lng: e.latlng.lng,
        url: `https://www.openstreetmap.org/?mlat=${e.latlng.lat}&mlon=${e.latlng.lng}#map=18/${e.latlng.lat}/${e.latlng.lng}`,
      })
    },
  })

  return position === null ? null : <Marker position={position} icon={customIcon} />
}

const MapPicker = ({ onLocationSelect, initialLocation = null }) => {
  const [position, setPosition] = useState(initialLocation ? [initialLocation.lat, initialLocation.lng] : null)
  const [address, setAddress] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const mapRef = useRef()

  // Default center (Addis Ababa, Ethiopia)
  const defaultCenter = [9.0192, 38.7525]

  // Get user's current location
  const getCurrentLocation = () => {
    setIsLoading(true)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          const newPos = [lat, lng]
          setPosition(newPos)

          // Reverse geocode to get address
          let address = ""
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
            )
            const data = await response.json()
            address = data.display_name || ""
            setAddress(address)
          } catch (error) {
            console.error("Reverse geocoding error:", error)
          }

          onLocationSelect({
            lat,
            lng,
            url: `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`,
            address,
          })

          // Center map on new location
          if (mapRef.current) {
            mapRef.current.setView(newPos, 15)
          }
          setIsLoading(false)
        },
        (error) => {
          console.error("Error getting location:", error)
          setIsLoading(false)
          alert("Unable to get your current location. Please click on the map to select a location.")
        }
      )
    } else {
      setIsLoading(false)
      alert("Geolocation is not supported by this browser.")
    }
  }

  // Search for location using Nominatim API
  const searchLocation = async () => {
    if (!searchQuery.trim()) return

    setIsLoading(true)
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
      )
      const data = await response.json()

      if (data && data.length > 0) {
        const result = data[0]
        const newPos = [Number.parseFloat(result.lat), Number.parseFloat(result.lon)]
        const address = result.display_name
        setPosition(newPos)
        setAddress(result.display_name)
        onLocationSelect({
          lat: Number.parseFloat(result.lat),
          lng: Number.parseFloat(result.lon),
          url: `https://www.openstreetmap.org/?mlat=${result.lat}&mlon=${result.lon}#map=18/${result.lat}/${result.lon}`,
          address: result.display_name,
        })
        // Center map on search result
        if (mapRef.current) {
          mapRef.current.setView(newPos, 15)
        }
      } else {
        alert("Location not found. Please try a different search term.")
      }
    } catch (error) {
      console.error("Search error:", error)
      alert("Error searching for location. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  // Reverse geocoding to get address from coordinates
  const getAddressFromCoords = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      )
      const data = await response.json()
      if (data && data.display_name) {
        setAddress(data.display_name)
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error)
    }
  }

  // Update address when position changes
  useEffect(() => {
    if (position) {
      getAddressFromCoords(position[0], position[1])
    }
  }, [position])

  const clearLocation = () => {
    setPosition(null)
    setAddress("")
    setSearchQuery("")
    onLocationSelect(null)
  }

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search for a location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && searchLocation()}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={searchLocation}
            disabled={isLoading || !searchQuery.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? "..." : "Search"}
          </button>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isLoading}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {isLoading ? "Getting Location..." : "Use Current Location"}
          </button>

          {position && (
            <button
              type="button"
              onClick={clearLocation}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Clear Location
            </button>
          )}
        </div>
      </div>

      {/* Selected Location Info */}
      {position && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-1">Selected Location:</h4>
          <p className="text-sm text-blue-700">
            Coordinates: {position[0].toFixed(6)}, {position[1].toFixed(6)}
          </p>
          {address && <p className="text-sm text-blue-700 mt-1">Address: {address}</p>}
        </div>
      )}

      {/* Map Container */}
      <div className="h-96 w-full border border-gray-300 rounded-lg overflow-hidden">
        <MapContainer
          center={position || defaultCenter}
          zoom={position ? 15 : 10}
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker position={position} setPosition={setPosition} onLocationSelect={onLocationSelect} />
        </MapContainer>
      </div>

      {/* Instructions */}
      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
        <p className="font-medium mb-1">How to select your location:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Click anywhere on the map to place a marker</li>
          <li>Use the search box to find a specific address or place</li>
          <li>Click "Use Current Location" to automatically detect your position</li>
          <li>The selected coordinates and address will be saved with your profile</li>
        </ul>
      </div>
    </div>
  )
}

export default MapPicker
